import { Link, useLocation } from "react-router-dom";
import fixswiftLogo from "@/assets/fixswift-logo.svg";
import { ShoppingCart, Search, User, Menu, X, LogOut, Shield, ChevronRight, Package, Wrench, Calculator, Laptop, Award, HelpCircle, Repeat, Bell, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useState, useEffect, useCallback } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent } from "./ui/sheet";
import { cn } from "@/lib/utils";

const navLinks = [
  { to: "/products", label: "Shop All" },
  { to: "/why-refurbished", label: "Why Refurbished?" },
  { to: "/certification", label: "Certification" },
  { to: "/sell", label: "Sell Device" },
  { to: "/repair", label: "Repair" },
  { to: "/blog", label: "Blog" },
];

const mobileMenuItems = [
  { to: "/products", label: "Shop All", icon: Laptop },
  { to: "/why-refurbished", label: "Why Refurbished?", icon: Award },
  { to: "/certification", label: "Certification", icon: Shield },
  { to: "/sell", label: "Sell Device", icon: Repeat },
  { to: "/repair", label: "Repair", icon: Wrench },
  { to: "/blog", label: "Blog", icon: BookOpen },
  { to: "/emi-calculator", label: "EMI Calculator", icon: Calculator },
  { to: "/bulk-order", label: "Bulk Orders", icon: Package },
  { to: "/repair/track", label: "Track Order", icon: Search },
  { to: "/contact-us", label: "Support", icon: HelpCircle },
];

const Navbar = () => {
  const { totalItems } = useCart();
  const { user, isAdmin, signOut } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMobile = useCallback(() => setMobileOpen(false), []);
  const toggleMobile = useCallback(() => setMobileOpen((v) => !v), []);

  return (
    <header
      className={cn(
        "sticky top-0 z-50 bg-card/95 backdrop-blur-md supports-[backdrop-filter]:bg-card/85 transition-all duration-300",
        scrolled ? "shadow-sm border-b border-border/60" : "border-b border-border/30"
      )}
      style={{ minHeight: "56px", contain: "layout style", paddingTop: "env(safe-area-inset-top, 0px)" }}
    >
      <div className="container flex h-14 items-center gap-2">
        {/* Left – Logo */}
        <div className="shrink-0 lg:w-40">
          <Link to="/" className="flex items-center gap-2 font-heading text-lg font-black tracking-tight text-foreground group w-fit">
            <img src={fixswiftLogo} alt="Fixswift" className="h-8 w-8 group-hover:scale-105 transition-transform duration-300" width={32} height={32} />
            <span className="bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text">FixSwift</span>
          </Link>
        </div>

        {/* Center – Nav links (desktop) */}
        <nav className="hidden lg:flex flex-1 items-center justify-center gap-0.5" role="navigation" aria-label="Main navigation">
          {navLinks.map((link) => {
            const isActive = pathname === link.to || pathname.startsWith(link.to + "?");
            return (
              <Link
                key={link.to}
                to={link.to}
                className={cn(
                  "relative px-3.5 py-2 text-[13px] font-bold transition-colors rounded-full",
                  isActive
                    ? "text-primary bg-primary/8"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                )}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>

        {/* Right – Actions */}
        <div className="shrink-0 lg:w-40 flex items-center justify-end gap-0.5 ml-auto">
          {/* Desktop search */}
          <Link to="/products" aria-label="Search products" className="hidden md:block">
            <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-muted/60 rounded-full transition-colors">
              <Search className="h-[18px] w-[18px]" strokeWidth={2.2} />
            </Button>
          </Link>

          {/* Cart with badge */}
          <Link to="/cart" className="relative group" aria-label="Shopping cart">
            <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground group-hover:text-foreground hover:bg-muted/60 rounded-full transition-colors">
              <ShoppingCart className="h-[18px] w-[18px]" strokeWidth={2.2} />
              {totalItems > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-accent text-[9px] font-black text-accent-foreground shadow-sm px-1">
                  {totalItems > 9 ? "9+" : totalItems}
                </span>
              )}
            </Button>
          </Link>

          {/* User */}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-muted/60 rounded-full transition-colors" aria-label="Account menu">
                  <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-3.5 w-3.5 text-primary" strokeWidth={2.5} />
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 rounded-2xl p-0 shadow-elevated border border-border/80 overflow-hidden">
                {/* Profile header with gradient */}
                <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent px-4 pt-4 pb-3">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-primary/15 ring-2 ring-primary/20 flex items-center justify-center shrink-0">
                      <span className="text-sm font-black text-primary uppercase">
                        {(user.user_metadata?.full_name || user.email || "U").charAt(0)}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-foreground truncate leading-tight">
                        {user.user_metadata?.full_name || user.email?.split("@")[0]}
                      </p>
                      <p className="text-[11px] text-muted-foreground truncate mt-0.5">{user.email}</p>
                    </div>
                  </div>
                </div>

                <div className="p-1.5">
                  <DropdownMenuItem asChild className="rounded-xl cursor-pointer text-sm font-semibold text-foreground hover:bg-muted py-2.5 px-3 gap-2.5">
                    <Link to="/account">
                      <User className="h-4 w-4 text-muted-foreground" /> My Account
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/40 ml-auto" />
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem asChild className="rounded-xl cursor-pointer text-sm font-semibold text-foreground hover:bg-muted py-2.5 px-3 gap-2.5">
                      <Link to="/admin/dashboard">
                        <Shield className="h-4 w-4 text-primary" /> Admin Dashboard
                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/40 ml-auto" />
                      </Link>
                    </DropdownMenuItem>
                  )}
                </div>

                <div className="border-t border-border/60 p-1.5">
                  <DropdownMenuItem onClick={signOut} className="rounded-xl cursor-pointer text-sm font-bold text-destructive hover:bg-destructive/8 py-2.5 px-3 gap-2.5">
                    <LogOut className="h-4 w-4" /> Sign Out
                  </DropdownMenuItem>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/auth" aria-label="Sign in">
              <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-muted/60 rounded-full transition-colors">
                <User className="h-[18px] w-[18px]" strokeWidth={2.2} />
              </Button>
            </Link>
          )}

          {/* Mobile menu toggle */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-muted/60 rounded-full transition-colors"
            onClick={toggleMobile}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            {mobileOpen ? <X className="h-[18px] w-[18px]" strokeWidth={2.5} /> : <Menu className="h-[18px] w-[18px]" strokeWidth={2.5} />}
          </Button>
        </div>
      </div>

      {/* ── Mobile Side Menu ── */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-[300px] sm:w-[340px] bg-background border-r border-border p-0 flex flex-col">
          {/* Header */}
          <div className="flex items-center gap-2.5 px-5 h-14 border-b border-border shrink-0">
            <img src={fixswiftLogo} alt="Fixswift" className="h-7 w-7" width={28} height={28} />
            <span className="font-heading text-base font-black tracking-tight text-foreground">FixSwift</span>
          </div>

          {/* Profile card */}
          <div className="px-5 py-4 border-b border-border shrink-0">
            {user ? (
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary font-heading font-bold text-sm">
                  <User className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-heading text-sm font-bold text-foreground truncate">
                    {user.user_metadata?.full_name || user.email?.split("@")[0] || "User"}
                  </p>
                  <p className="text-[11px] text-muted-foreground truncate">{user.email}</p>
                </div>
              </div>
            ) : (
              <Link to="/auth" onClick={closeMobile} className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <User className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <p className="font-heading text-sm font-bold text-foreground">Welcome!</p>
                  <p className="text-[11px] text-primary font-semibold">Sign in for best experience →</p>
                </div>
              </Link>
            )}
          </div>

          {/* Menu items */}
          <div className="flex-1 overflow-y-auto py-1">
            {mobileMenuItems.map((item) => {
              const isActive = pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={closeMobile}
                  className={cn(
                    "flex items-center gap-3.5 px-5 py-3 text-sm font-semibold transition-colors",
                    isActive
                      ? "text-primary bg-primary/5 border-r-2 border-primary"
                      : "text-foreground hover:bg-muted/50 active:bg-muted"
                  )}
                >
                  <item.icon className={cn("h-[18px] w-[18px] shrink-0", isActive ? "text-primary" : "text-muted-foreground")} strokeWidth={2} />
                  <span className="flex-1">{item.label}</span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground/40 shrink-0" />
                </Link>
              );
            })}

            {isAdmin && (
              <>
                <div className="h-px bg-border mx-5 my-2" />
                <Link
                  to="/admin/dashboard"
                  onClick={closeMobile}
                  className="flex items-center gap-3.5 px-5 py-3 text-sm font-semibold text-foreground hover:bg-muted/50 active:bg-muted transition-colors"
                >
                  <Shield className="h-[18px] w-[18px] text-primary shrink-0" strokeWidth={2} />
                  <span className="flex-1">Admin Dashboard</span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground/40 shrink-0" />
                </Link>
              </>
            )}
          </div>

          {/* Bottom */}
          <div className="shrink-0 p-4 border-t border-border bg-background">
            {user ? (
              <div className="flex gap-2">
                <Link to="/account" onClick={closeMobile} className="flex-1">
                  <Button variant="outline" className="w-full rounded-full h-11 font-bold text-sm">
                    My Account
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  onClick={() => { signOut(); closeMobile(); }}
                  className="h-11 w-11 rounded-full text-destructive hover:bg-destructive/10 shrink-0"
                  size="icon"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Link to="/auth" onClick={closeMobile}>
                <Button className="w-full rounded-full h-11 font-bold text-sm">
                  Sign In / Sign Up
                </Button>
              </Link>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
};

export default Navbar;
